import { useState, useCallback, useRef } from 'react';
import { workloadsApi } from '../../../../services/workloadsApi';
import { useClient } from '../../../../contexts/ClientContext';
import { useAuth } from '../../../../contexts/AuthContext';

export function useTagAutocomplete() {
  const { user } = useAuth();
  const { selectedClientId } = useClient();
  const [keySuggestions, setKeySuggestions] = useState<string[]>([]);
  const [valueSuggestions, setValueSuggestions] = useState<string[]>([]);
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);
  const [isLoadingValues, setIsLoadingValues] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const effectiveClientId = user?.role === 'client' ? user.client?.id : selectedClientId;

  const searchKeys = useCallback((prefix: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!effectiveClientId) return;
      setIsLoadingKeys(true);
      try {
        const results = await workloadsApi.getTagKeys(prefix, effectiveClientId);
        setKeySuggestions(results);
      } catch {
        setKeySuggestions([]);
      } finally {
        setIsLoadingKeys(false);
      }
    }, 300);
  }, [effectiveClientId]);

  const searchValues = useCallback((key: string, prefix: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!effectiveClientId || !key) return;
      setIsLoadingValues(true);
      try {
        const results = await workloadsApi.getTagValues(key, prefix, effectiveClientId);
        setValueSuggestions(results);
      } catch {
        setValueSuggestions([]);
      } finally {
        setIsLoadingValues(false);
      }
    }, 300);
  }, [effectiveClientId]);

  return {
    keySuggestions,
    valueSuggestions,
    isLoadingKeys,
    isLoadingValues,
    searchKeys,
    searchValues,
  };
}
